import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Tabs } from '../atoms/Tabs/Tabs';
import { PhotoUploadDropzone } from '../atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { useGalleryImages } from '../../hooks/useGalleryImages';
import { uploadGalleryImages } from '../../services/galleryService';
import { linkPhotoToEntry } from '../../services/journalService';
import { useTranslation } from '../../i18n/useTranslation';
import './AddPhotoControl.css';

export interface AddPhotoControlProps {
  aquariumId: string;
  entryId: string;
  /** Ids of the images already linked to the entry — shown as attached in the picker. */
  attachedImageIds?: string[];
  /** Called after a photo was linked to the entry. */
  onAttached?: () => void;
}

type TabId = 'upload' | 'gallery';

type Status =
  | { kind: 'idle' }
  | { kind: 'busy'; messageKey: string }
  | { kind: 'done'; messageKey: string }
  | { kind: 'error'; messageKey: string };

const UPLOAD_TAB_ID = 'journal-photo-upload';
const GALLERY_TAB_ID = 'journal-photo-gallery';

/**
 * Attaches a photo to a saved journal entry, in two ways:
 *  - upload a new image into the aquarium gallery, then link it to the entry
 *    (two-step upload-then-link — there is no separate journal upload endpoint);
 *  - pick an existing gallery image and link it.
 * Progress and errors are announced in a polite live region.
 */
export function AddPhotoControl({
  aquariumId,
  entryId,
  attachedImageIds = [],
  onAttached,
}: AddPhotoControlProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('upload');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [dropzoneKey, setDropzoneKey] = useState(0);

  const gallery = useGalleryImages({ aquariumId, enabled: activeTab === 'gallery' });
  const isBusy = status.kind === 'busy';
  const attached = new Set(attachedImageIds);

  const link = useCallback(
    async (imageId: string): Promise<boolean> => {
      try {
        await linkPhotoToEntry(aquariumId, entryId, imageId);
      } catch {
        setStatus({ kind: 'error', messageKey: 'journal.attachments.linkError' });
        return false;
      }
      setStatus({ kind: 'done', messageKey: 'journal.attachments.attached' });
      onAttached?.();
      return true;
    },
    [aquariumId, entryId, onAttached],
  );

  const handleFileSelected = useCallback(
    async (file: File) => {
      setStatus({ kind: 'busy', messageKey: 'journal.attachments.uploading' });
      let imageId: string | undefined;
      try {
        const uploaded = await uploadGalleryImages(aquariumId, [file]);
        imageId = uploaded[0]?.id;
      } catch {
        imageId = undefined;
      }
      if (!imageId) {
        setStatus({ kind: 'error', messageKey: 'journal.attachments.uploadError' });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['galleryImages', aquariumId] });
      setStatus({ kind: 'busy', messageKey: 'journal.attachments.linking' });
      if (await link(imageId)) {
        // Reset the dropzone so its preview does not suggest a pending upload.
        setDropzoneKey((k) => k + 1);
      }
    },
    [aquariumId, link, queryClient],
  );

  const handlePick = useCallback(
    async (imageId: string) => {
      setStatus({ kind: 'busy', messageKey: 'journal.attachments.linking' });
      await link(imageId);
    },
    [link],
  );

  const uploadPanel = (
    <div className="add-photo-control__panel">
      <PhotoUploadDropzone
        key={dropzoneKey}
        onChange={(file) => void handleFileSelected(file)}
        disabled={isBusy}
        ariaLabel={t('journal.attachments.dropzoneLabel')}
      />
    </div>
  );

  let galleryBody;
  if (gallery.isLoading) {
    galleryBody = <Text color="muted">{t('journal.attachments.galleryLoading')}</Text>;
  } else if (gallery.error) {
    galleryBody = (
      <Alert compact>{t('journal.attachments.galleryError')}</Alert>
    );
  } else if (gallery.images.length === 0) {
    galleryBody = <Text color="muted">{t('journal.attachments.galleryEmpty')}</Text>;
  } else {
    galleryBody = (
      <>
        <ul className="add-photo-control__picker" aria-label={t('journal.attachments.galleryTab')}>
          {gallery.images.map((image) => {
            const isAttached = attached.has(image.id);
            const src = image.thumbnailUrl ?? image.displayUrl ?? undefined;
            return (
              <li key={image.id} className="add-photo-control__picker-item">
                <Card padding="sm" className="add-photo-control__picker-card">
                  <img
                    src={src}
                    alt={t('journal.attachments.galleryPhotoAlt', { name: image.originalFilename })}
                    className="add-photo-control__picker-image"
                    loading="lazy"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant={isAttached ? 'ghost' : 'secondary'}
                    disabled={isAttached || isBusy}
                    aria-label={
                      isAttached
                        ? t('journal.attachments.alreadyAttachedLabel', { name: image.originalFilename })
                        : t('journal.attachments.attachLabel', { name: image.originalFilename })
                    }
                    onClick={() => void handlePick(image.id)}
                  >
                    {isAttached ? t('journal.attachments.alreadyAttached') : t('journal.attachments.attach')}
                  </Button>
                </Card>
              </li>
            );
          })}
        </ul>
        {gallery.hasNextPage && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            isLoading={gallery.isFetchingNextPage}
            onClick={() => gallery.fetchNextPage()}
          >
            {t('journal.attachments.loadMore')}
          </Button>
        )}
      </>
    );
  }

  const galleryPanel = <div className="add-photo-control__panel">{galleryBody}</div>;

  return (
    <div className="add-photo-control" data-testid="add-photo-control">
      <Tabs
        aria-label={t('journal.attachments.tabsLabel')}
        activeId={activeTab === 'upload' ? UPLOAD_TAB_ID : GALLERY_TAB_ID}
        onChange={(id) => setActiveTab(id === GALLERY_TAB_ID ? 'gallery' : 'upload')}
        items={[
          { id: UPLOAD_TAB_ID, label: t('journal.attachments.uploadTab'), panel: uploadPanel },
          { id: GALLERY_TAB_ID, label: t('journal.attachments.galleryTab'), panel: galleryPanel },
        ]}
      />
      {status.kind === 'error' && (
        <Alert compact data-testid="add-photo-control-error">
          {t(status.messageKey)}
        </Alert>
      )}
      <LiveRegion className="add-photo-control__status" data-testid="add-photo-control-status">
        {status.kind !== 'idle' && status.kind !== 'error' && (
          <Text size="sm" color="secondary">
            {t(status.messageKey)}
          </Text>
        )}
      </LiveRegion>
    </div>
  );
}
